// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import CustomFieldInput from '../../components/CustomFieldInput'
import { ArrowLeft, Stethoscope, Phone, Building2 } from 'lucide-react'
import * as api from '../../api/client'
import { useDrugRefValues } from '../../hooks/useDrugRefValues'
import { useRxSideValuesAll } from '../../hooks/useRxSideValuesAll'
import CategoryBadge from '../../components/common/CategoryBadge'
import { friendlyError } from '../../api/errors'
import { formatNpi, formatDea, isSyntheticNpi, formatPhone, formatCell } from '../../api/format'
import { ClinicalStatePill, PayerStatePill, ErrorBanner, LoadingState, EmptyState, relativeTime } from '../../components/workflow/StatePills'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { useAuth } from '../../auth/AuthContext'
import { usePrescriberSiteOfCare } from '../../hooks/queries/siteOfCare'
import { useModules } from '../../auth/ModulesContext'
import OtherEmailsSection from './OtherEmailsSection'

export default function PrescriberDetailPage() {
  const { id } = useParams<{ id: string }>()
  const socQ = usePrescriberSiteOfCare(id)   // D-SOC-2: unconditional, above the early returns
  const navigate = useNavigate()
  const { hasPermission, me } = useAuth()
  // ASP #26: alternate emails are a CareRx feature (mig 420) — shown where CareRx is on and allowed.
  const { isDisabled } = useModules()
  const showOtherEmails = hasPermission('carerx.access') && !isDisabled('carerx')
  const [data, setData] = useState<api.PrescriberFullResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [layout, setLayout] = useState<api.RxViewLayout | null>(null)
  // Active Prescriptions is an Rx table → honor the Rx-header customization (e.g. MRN hidden).
  const [rxLayout, setRxLayout] = useState<api.RxViewLayout | null>(null)
  void rxLayout
  const [cfv, setCfv] = useState<api.CustomFieldValues | null>(null)
  const [emv, setEmv] = useState<api.EntityMappedValues | null>(null)

  useEffect(() => { api.getRxViewLayout(undefined, 'prescriber').then(setLayout).catch(() => setLayout(null)) }, [])
  useEffect(() => { api.getRxViewLayout(undefined, 'rx').then(setRxLayout).catch(() => setRxLayout(null)) }, [])
  // BA-31: the prescriber card Active Prescriptions table has its own surface (presc_rx_list),
  // managed in the Prescriber view builder.
  const [rxColsCfg, setRxColsCfg] = useState<api.RxViewLayout | null>(null)
  useEffect(() => { api.getRxViewLayout(undefined, 'presc_rx_list').then(setRxColsCfg).catch(() => setRxColsCfg(null)) }, [])
  useEffect(() => { if (id) api.getCustomFieldValues('prescriber', id).then(setCfv).catch(() => setCfv(null)) }, [id])
  useEffect(() => { if (id) api.getEntityMappedValues('prescriber', id).then(setEmv).catch(() => setEmv(null)) }, [id])

  useEffect(() => {
    if (!id) return
    setLoading(true); setError(null)
    api.getPrescriber(id)
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div style={pageStyle}><LoadingState label="Loading prescriber…" /></div>
  if (error)   return <div style={pageStyle}><ErrorBanner message={error} /></div>
  if (!data)   return null

  const { prescriber, rx } = data
  const activeRx = rx.filter(r => !['delivered','cancelled','rejected'].includes(r.state))
  const historicalRx = rx.filter(r => ['delivered','cancelled','rejected'].includes(r.state))

  // Custom layout rendering (opt-in). Mirrors the hardcoded Credentials/Practice/Contact
  // cards; a pharmacy keeps the built-in view until it saves a Prescriber layout.
  const cardIcon = (key: string): any =>
    ({ credentials: Stethoscope, practice: Building2, contact: Phone } as any)[key] ?? Stethoscope
  const layoutFieldValue = (f: any): string => {
    const col = f.owlrx_column
    // D-SOC-2: aggregate over the prescriber's patients (GET /prescribers/{id}/site-of-care).
    if (f.owlrx_table === 'prescriber_site_of_care') {
      const a = socQ.data
      if (!a || !a.available) return socQ.isLoading ? '…' : '—'
      if (col === 'facilities_served') return a.facilities_served ?? 'None'
      if (col === 'facility_patient_count') return String(a.facility_patient_count)
      if (col === 'residential_patient_count') return String(a.residential_patient_count)
      return '—'
    }
    if (col === 'npi') return formatNpi(prescriber.npi as string) || '—'
    if (col === 'dea_number') return formatDea(prescriber.dea_number as string) || '—'
    if (col === 'address')
      return [prescriber.address_line, prescriber.city, prescriber.state_code, prescriber.postal_code].filter(Boolean).join(', ') || '—'
    const v = (prescriber as any)[col]
    return formatCell(col, v)
  }
  const myRoles = new Set(me?.authenticated ? (me.role_ids?.length ? me.role_ids : (me.primary_role_id ? [me.primary_role_id] : [])) : [])
  const isPlatformAdmin = hasPermission('platform.access')
  const regionCards = (region: string) => (layout?.cards ?? []).filter((c: any) => (c?.config?.region ?? 'body') === region)
  const bandAllow = api.headerBandAllow(layout?.cards as any)
  const showCore = (k: string) => !bandAllow || bandAllow.includes(k)
  const renderCards = (cardsList: any[]) => cardsList.map((card: any) => {
    if (!card.visible) return null
    if (!isPlatformAdmin) {
      if (Array.isArray(card.denied_roles) && card.denied_roles.some((r: string) => myRoles.has(r))) return null
      if (Array.isArray(card.allowed_roles) && card.allowed_roles.length > 0
          && !card.allowed_roles.some((r: string) => myRoles.has(r))) return null
    }
    const vis = (card.fields ?? []).filter((f: any) => f.visible)
    if (!vis.length) return null
    const Icon = cardIcon(card.card_key)
    return (
      <Section key={card.card_key} icon={Icon} title={card.title}>
        {vis.map((f: any) => <Field key={`${f.owlrx_table}.${f.owlrx_column}`} label={f.owlrx_label}>{f.pioneer_path ? (emv?.values?.[`${card.card_key}.${f.owlrx_column}`] || '—') : ((f.owlrx_table === 'custom') ? <CustomFieldInput surface="prescriber" recordId={id || ''} cardKey={card.card_key} field={f} initialValue={cfv?.values?.[`${card.card_key}.${f.owlrx_column}`]} canWrite={!!cfv?.can_write} canModify={!!cfv?.can_modify} /> : layoutFieldValue(f))}</Field>)}
      </Section>
    )
  })

  const renderHeaderBand = (headerCards: any[]) => {
    const fs = (headerCards || []).filter((c: any) => c.visible).flatMap((c: any) => (c.fields ?? []).filter((f: any) => f.visible))
    const nameF = fs.find((f: any) => f.owlrx_column === 'display_name')
    const suffixF = fs.find((f: any) => f.owlrx_column === 'suffix')
    const statusF = fs.find((f: any) => f.owlrx_column === 'status')
    const meta = fs.filter((f: any) => !['display_name', 'suffix', 'status'].includes(f.owlrx_column))
    return (
      <div style={headerCard}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={titleStyle}>{nameF ? layoutFieldValue(nameF) : (prescriber.display_name as string)}</h1>
          {suffixF && prescriber.suffix && <span style={mutedSm}>{layoutFieldValue(suffixF)}</span>}
          {statusF && prescriber.status && <span style={statusBadge}>{layoutFieldValue(statusF)}</span>}
        </div>
        {meta.length > 0 && (
          <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: 'var(--color-text-soft)', flexWrap: 'wrap' }}>
            {meta.map((f: any) => <span key={f.owlrx_column}><strong style={{ color: 'var(--color-text)' }}>{f.owlrx_label}</strong> {layoutFieldValue(f)}</span>)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      <button onClick={() => navigate(-1)} style={backBtn}>
        <ArrowLeft size={14} aria-hidden="true" /> Back
      </button>

      {regionCards('header').length > 0 ? (
        renderHeaderBand(regionCards('header'))
      ) : (
      <div style={headerCard}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={titleStyle}>{prescriber.display_name as string}</h1>
          {showCore('suffix') && prescriber.suffix && <span style={mutedSm}>{prescriber.suffix as string}</span>}
          {showCore('status') && prescriber.status && <span style={statusBadge}>{prescriber.status as string}</span>}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: 'var(--color-text-soft)', flexWrap: 'wrap' }}>
          {showCore('npi') && <span><strong style={{ color: 'var(--color-text)' }}>NPI</strong> <span style={mono}>{formatNpi(prescriber.npi as string)}</span></span>}
          {showCore('dea_number') && prescriber.dea_number && !isSyntheticNpi(prescriber.dea_number as string) && (
            <span><strong style={{ color: 'var(--color-text)' }}>DEA</strong> <span style={mono}>{formatDea(prescriber.dea_number as string)}</span></span>
          )}
          {showCore('specialty') && prescriber.specialty && <span><strong style={{ color: 'var(--color-text)' }}>Specialty</strong> {prescriber.specialty as string}</span>}
        </div>
      </div>
      )}

      <div style={gridStyle}>
        {layout?.source === 'custom' ? renderCards(regionCards('body')) : (<>
        <Section icon={Stethoscope} title="Credentials">
          <Field label="NPI">{formatNpi(prescriber.npi as string)}</Field>
          <Field label="DEA">{formatDea(prescriber.dea_number as string)}</Field>
          <Field label="State license">{(prescriber.state_license as string) ?? '—'}</Field>
          <Field label="Type">{(prescriber.prescriber_type as string) ?? '—'}</Field>
          <Field label="Specialty">{(prescriber.specialty as string) ?? '—'}</Field>
          <Field label="Status">{(prescriber.status as string) ?? '—'}</Field>
        </Section>

        <Section icon={Building2} title="Practice">
          <Field label="Clinic">{(prescriber.clinic_name as string) ?? '—'}</Field>
          <Field label="Address">{
            [prescriber.address_line, prescriber.city, prescriber.state_code, prescriber.postal_code]
              .filter(Boolean).join(', ') || '—'
          }</Field>
        </Section>

        {/* D-SOC-2: a prescriber's site of care is an aggregate of their patients' — shown on the default
            view too, so a pharmacy without a custom prescriber layout still sees it. */}
        <Section icon={Building2} title="Site of care">
          {socQ.isLoading ? <span style={mutedSm}>Loading…</span>
            : (!socQ.data?.available) ? <span style={mutedSm}>Not available on this pharmacy.</span>
            : (<>
              <Field label="Facilities served">{socQ.data.facilities_served ?? <span style={mutedSm}>None</span>}</Field>
              <Field label="Patients at facilities">{String(socQ.data.facility_patient_count)}</Field>
              <Field label="Patients at home">{String(socQ.data.residential_patient_count)}</Field>
            </>)}
        </Section>
        <Section icon={Phone} title="Contact">
          <Field label="Phone">{formatPhone(prescriber.primary_phone as string)}</Field>
          <Field label="Fax">{(prescriber.primary_fax as string) ?? '—'}</Field>
          <Field label="Email">{(prescriber.email as string) ?? '—'}</Field>
          {/* ASP #26: alternate emails a CareRx pharmacy adds (mig 420) — PioneerRx's email is untouched. */}
          {showOtherEmails && id && <OtherEmailsSection subjectType="prescriber" subjectId={id} />}
        </Section>
        </>)}
      </div>

      {layout?.source === 'custom' && regionCards('footer').length > 0 && (
        <div style={gridStyle}>{renderCards(regionCards('footer'))}</div>
      )}

      {/* Team — other prescribers at this clinic OR address (WI-908 grouping) */}
      {data.team && data.team.length > 0 && (
        <>
          <h2 style={sectionH2}>
            {data.team_match_kind === 'address'
              ? `Team at ${prescriber.address_line ?? ''}, ${prescriber.city ?? ''} ${prescriber.state_code ?? ''}`.trim()
              : `Team at ${(prescriber.clinic_name as string) ?? 'this practice'}`}
            {' '}({data.team.length})
          </h2>
          <div style={teamGridStyle}>
            {data.team.map(t => (
              <div
                key={t.id}
                style={teamCardStyle}
                onClick={() => navigate(`/prescribers/${t.id}`)}
                onKeyDown={e => { if (e.key === 'Enter') navigate(`/prescribers/${t.id}`) }}
                tabIndex={0}
                role="button"
                aria-label={`Open ${t.display_name}`}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>{t.display_name}</div>
                {t.specialty && <div style={mutedSm}>{t.specialty}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: 'var(--color-text-soft)', flexWrap: 'wrap' }}>
                  {t.rx_total > 0 && <span><strong>{t.rx_total}</strong> Rx</span>}
                  {t.npi && !isSyntheticNpi(t.npi) && <span style={mono}>NPI {t.npi}</span>}
                  {t.primary_phone && <span>{formatPhone(t.primary_phone)}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {data.merged_ids && data.merged_ids.length > 1 && (
        <div style={mergedNoticeStyle}>
          This prescriber profile aggregates {data.merged_ids.length} PioneerRx records that appear to be the same person
          (same name + clinic). Their prescriptions are combined below.
        </div>
      )}

      <h2 style={sectionH2}>Active prescriptions ({activeRx.length})</h2>
      <div style={cardStyle}>
        {activeRx.length === 0 ? (
          <EmptyState message="No active prescriptions written." />
        ) : <RxTable rows={activeRx} cfg={rxColsCfg} onOpenRx={rxId => navigate(`/rx/${rxId}`)} onOpenPatient={pid => navigate(`/patients/${pid}`)} />}
      </div>

      {historicalRx.length > 0 && (
        <>
          <h2 style={sectionH2}>History ({historicalRx.length})</h2>
          <div style={cardStyle}>
            <RxTable rows={historicalRx} cfg={rxColsCfg} onOpenRx={rxId => navigate(`/rx/${rxId}`)} onOpenPatient={pid => navigate(`/patients/${pid}`)} />
          </div>
        </>
      )}
    </div>
  )
}

// D-UI-2: what each Rx cell SHOWS, as text, for the column funnels. The pill labels mirror
// ClinicalStatePill / PayerStatePill (StatePills.tsx does not export them); an unknown code falls
// back to the code itself, exactly as the pills do.
const RX_CLINICAL_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', verified: 'Verified', ready_to_fill: 'Ready to fill',
  dispensed: 'Dispensed', out_for_delivery: 'Out for delivery', delivered: 'Delivered',
  on_hold: 'On hold', cancelled: 'Cancelled', rejected: 'Rejected',
}
const RX_PAYER_LABELS: Record<string, string> = {
  not_required: 'No insurance', awaiting_insurance: 'Awaiting ins.', pa_required: 'PA needed',
  pa_pending: 'PA pending', pa_approved: 'PA approved', pa_denied: 'PA denied',
  insurance_rejected: 'Ins. rejected', cash_pay: 'Cash pay',
}
function rxCellText(r: api.PrescriberFullResponse['rx'][number], key: string): unknown {
  switch (key) {
    case 'rx_number': return r.rx_number
    case 'patient_name': return r.patient_name
    case 'drug_name': return r.drug_name
    case 'sig': return r.sig
    case 'quantity_authorized': return [r.quantity_authorized, r.quantity_unit].filter(v => v != null && v !== '').join(' ')
    case 'state': return RX_CLINICAL_LABELS[r.state] ?? r.state
    case 'payer_state': return RX_PAYER_LABELS[r.payer_state] ?? r.payer_state
    case 'last_activity': return r.last_state_change_at   // raw: the funnel compares by day
    default: return (r as any)[key]   // the cell shows the raw value
  }
}

function RxTable({
  rows, onOpenRx, cfg,
}: {
  rows: api.PrescriberFullResponse['rx']
  onOpenRx: (id: string) => void
  onOpenPatient: (id: string) => void
  cfg?: api.RxViewLayout | null
}) {
  // BA-31: columns come from the presc_rx_list surface (Prescriber view builder);
  // fallback = the historical fixed set. Custom / PioneerRx-mapped columns render
  // values from the extraction store.
  const card = cfg?.cards?.find(c => c.card_key === 'list_columns')
  const cfgFields = (cfg?.source === 'custom' ? (card?.fields ?? []) : []).filter(f => f.visible)
  const subHidden = new Set<string>((((card?.config as any)?.subtext_hidden ?? []) as string[]))
  const FALLBACK: [string, string][] = [
    ['rx_number', 'Rx #'], ['patient_name', 'Patient'], ['drug_name', 'Drug'], ['sig', 'Sig'],
    ['quantity_authorized', 'Quantity'], ['state', 'Clinical'], ['payer_state', 'Payer'], ['last_activity', 'Last activity'],
  ]
  const defs = cfgFields.length > 0
    ? cfgFields.map(f => ({ key: f.owlrx_column, label: (f.owlrx_label || '').trim() || f.owlrx_column, custom: f.owlrx_table === 'custom', drugref: f.owlrx_table === 'drug_reference' }))
    : FALLBACK.map(([k, l]) => ({ key: k, label: l, custom: false, drugref: false }))
  const [cfv, setCfv] = useState<Record<string, Record<string, string | null>>>({})
  const hasCustom = defs.some(d => d.custom)
  // ASP: a prescriber/patient can have thousands of Rx; rendering (and resolving custom /
  // drug-ref values for) every row at once hangs the browser. Paginate — render and resolve
  // only the current page. Sorting/filtering still happen on the full `rows` upstream.
  // D-UI-2: funnels filter the WHOLE Rx set, then the result is paged. Custom / drug-reference
  // columns are resolved per PAGE for display (below), so their values do not exist for the rows
  // off screen. Opening a funnel on one of them loads the values for every row, once; until
  // then the column reads the page's values, which is what the cells show.
  const defsSig = defs.map(d => `${d.key}:${d.label}:${d.custom ? 'c' : d.drugref ? 'd' : ''}`).join('|')
  const allIdsKey = rows.map(r => r.id).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allIds = useMemo(() => rows.map(r => r.id), [allIdsKey])
  const side = useRxSideValuesAll(allIds, { custom: hasCustom, drugref: defs.some(d => d.drugref) })
  const funnelCols = useMemo<FunnelColumn<api.PrescriberFullResponse['rx'][number]>[]>(
    () => defs.map(d => d.custom
      ? { key: d.key, label: d.label, onOpen: side.load,
          value: r => (side.cfv[r.id] ?? cfv[r.id])?.[`list_columns.${d.key}`] ?? null }
      : d.drugref
      ? { key: d.key, label: d.label, onOpen: side.load,
          value: r => (side.dr[String(r.id).toLowerCase()] as any)?.[d.key] ?? null }
      : { key: d.key, label: d.label, value: r => rxCellText(r, d.key), kind: d.key === 'sig' ? 'text' as const : undefined }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defsSig, side.cfv, side.dr, side.load, cfv],
  )
  const fx = useTableFunnels(rows, funnelCols)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, fx.sig)
  const drugRefVals = useDrugRefValues(pageRows.map(r => r.id), defs.some(d => d.drugref))
  useEffect(() => {
    if (!hasCustom || pageRows.length === 0) return
    let dead = false
    api.batchCustomFieldValues('rx', pageRows.map(r => r.id)).then(r => { if (!dead) setCfv(r.values ?? {}) }).catch(() => {})
    return () => { dead = true }
  }, [hasCustom, pageRows])
  const cell = (r: api.PrescriberFullResponse['rx'][number], key: string) => {
    switch (key) {
      case 'rx_number': return <td key={key} style={tdStyle}><div style={mono}>{r.rx_number ?? '—'}</div></td>
      case 'patient_name': return (
        <td key={key} style={tdStyle}>
          <div style={{ fontWeight: 500 }}>{r.patient_name}</div>
        </td>)
      case 'drug_name': return (
        <td key={key} style={tdStyle}>
          <div style={{ fontWeight: 500 }}>
            {r.drug_name}
            {r.is_controlled && <span style={ctrlBadge}>CTRL</span>}
          </div>
          {!subHidden.has('drug_name') && <div style={mutedSm}>{r.drug_strength} · {r.drug_form}</div>}
        </td>)
      case 'sig': return <td key={key} style={{ ...tdStyle, maxWidth: 240 }}><div style={truncStyle}>{r.sig}</div></td>
      case 'quantity_authorized': return <td key={key} style={tdStyle}>{r.quantity_authorized} {r.quantity_unit}</td>
      case 'state': return <td key={key} style={tdStyle}><ClinicalStatePill state={r.state} /></td>
      case 'payer_state': return <td key={key} style={tdStyle}><PayerStatePill state={r.payer_state} /></td>
      case 'last_activity': return <td key={key} style={tdStyle}>{relativeTime(r.last_state_change_at)}</td>
      default: {
        const d = defs.find(x => x.key === key)
        if (d?.drugref) return <td key={key} style={tdStyle}>{key === 'category' ? <CategoryBadge name={drugRefVals[r.id]?.category} color={drugRefVals[r.id]?.category_color} /> : <span style={{ fontSize: 12.5 }}>{String((drugRefVals[r.id] as any)?.[key] ?? '—')}</span>}</td>
        if (d?.custom) return <td key={key} style={tdStyle}><span style={{ fontSize: 12.5 }}>{cfv[r.id]?.[`list_columns.${key}`] ?? '—'}</span></td>
        return <td key={key} style={tdStyle}>{String((r as any)[key] ?? '—')}</td>
      }
    }
  }
  return (
    <>
      <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="prescriptions" />
      {fx.chips}
      <table {...fx.tableProps} style={tableStyle}>
        <thead>
          <tr>{defs.map(d => <th key={d.key} style={thStyle} scope="col">{d.label} {fx.funnel(d.key)}</th>)}</tr>
        </thead>
        <tbody>
          {pageRows.map(r => (
            <tr key={r.id} style={trStyle} onClick={() => onOpenRx(r.id)}
                onKeyDown={e => { if (e.key === 'Enter') onOpenRx(r.id) }}
                tabIndex={0} role="button">
              {defs.map(d => cell(r, d.key))}
            </tr>
          ))}
        </tbody>
      </table>
      <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="prescriptions" />
    </>
  )
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <section style={{ background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-alt)', color: 'var(--color-text-soft)' }}>
        <Icon size={14} aria-hidden="true" />
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{title}</h3>
      </header>
      <div style={{ padding: '12px 16px 16px' }}>{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f5f5f4' }}>
      <div style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>{label}</div>
      <div style={{ color: 'var(--color-text)', wordBreak: 'break-word' }}>{children || '—'}</div>
    </div>
  )
}

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const backBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 12, color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit', marginBottom: 16,
}
const headerCard: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: 20, marginBottom: 16,
}
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const mutedSm: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const mono: React.CSSProperties = { fontFamily: 'monospace', fontSize: 12 }
const statusBadge: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: '2px 8px',
  background: '#dcfce7', color: '#166534', borderRadius: 4,
}
const gridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
  gap: 16, marginBottom: 16,
}
const sectionH2: React.CSSProperties = {
  fontSize: 16, fontWeight: 600, margin: '8px 0 12px', fontFamily: 'Georgia, serif',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden', marginBottom: 16,
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)', fontSize: 11, fontWeight: 600,
  letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = { borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }
const tdStyle: React.CSSProperties = { padding: '10px 14px', verticalAlign: 'middle' }
const truncStyle: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
const ctrlBadge: React.CSSProperties = {
  marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
  padding: '1px 6px', background: '#fef3c7', color: '#854d0e', borderRadius: 3,
}
const teamGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 12, marginBottom: 16,
}
const teamCardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, padding: '12px 14px',
  cursor: 'pointer', transition: 'border-color 120ms ease',
}
const mergedNoticeStyle: React.CSSProperties = {
  padding: '8px 12px', marginBottom: 12,
  background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
  borderRadius: 6, fontSize: 12, color: 'var(--color-text-soft)',
}
